import type { Brand, Capability, Manifest, Policy, View } from '@clawharbor/manifest';

/**
 * The dashboard's client.
 *
 * Two rules shape this file.
 *
 * The first is that the secret never leaves memory. It is the host's own
 * `CLAWHARBOR_RELOAD_SECRET`, which publishes, edits and unpublishes EVERY
 * tenant on the box — so it is held on this object for the life of the page and
 * written nowhere else. No `localStorage`, no `sessionStorage`, no cookie, and
 * never a query parameter: a URL ends up in history, in a proxy log and in a
 * screenshot, and there is no per-tenant scoping behind it to limit the damage.
 * An ACCOUNT is the other principal, and it travels differently: the server sets
 * an httpOnly `ch_session` cookie on the magic-link callback, so this page never
 * sees the session token at all — every request is sent with
 * `credentials: 'same-origin'` and the browser attaches it. The two are never
 * combined: the operator header is added only while a secret is actually held,
 * because a stale header on a cookie session would make the server answer for
 * the whole host when the person asked about their own tenants.
 *
 * The second is that a shape mismatch has to be LOUD. Every reader below names
 * the path it was reading when it failed, because the alternative is the failure
 * mode this dashboard exists to eliminate: a renamed field arrives as
 * `undefined`, React paints an empty cell, and an operator reads "0 calls" or a
 * blank health column as fact. A thrown `ContractError` puts the mismatch on the
 * screen with the field name in it. Where a field is genuinely optional the
 * reader returns null and the SCREEN says "never checked" — that is data, not a
 * fault, and the two are never collapsed.
 */

/** apps/server: `CLAWHARBOR_RELOAD_HEADER`, lower-cased, default below. */
const DEFAULT_SECRET_HEADER = 'x-clawharbor-reload-secret';

export type Verdict = 'healthy' | 'degraded' | 'broken' | 'unknown';

/** apps/server/src/usage/store.ts. Order is the order they are reported in. */
export const OUTCOMES = ['ok', 'empty', 'stale', 'rate_limited', 'error'] as const;
export type Outcome = (typeof OUTCOMES)[number];
export type OutcomeCounts = Record<Outcome, number>;

export interface ToolAggregate {
  /**
   * Which site the tool belongs to.
   *
   * The rollup keys on tenant AND tool, so a host-wide window returns one row
   * per pair and two sites that both call a tool `list_services` produce two
   * rows with the same name. Without this the table showed them as duplicates
   * and could not link either one anywhere.
   */
  tenantId: string | null;
  tool: string;
  calls: number;
  rows: number;
  ms: number;
  byOutcome: OutcomeCounts;
  /**
   * ISO of the most recent call in the window, null when there was none.
   *
   * A window total cannot say whether a tool went quiet: 400 calls over thirty
   * days reads identically whether the last was an hour ago or three weeks
   * ago, and the second is what a tool an agent gave up on looks like.
   */
  lastCalledAt: string | null;
}

export interface SeriesPoint {
  day: string;
  calls: number;
  rows: number;
  ms: number;
  byOutcome: OutcomeCounts;
}

export interface UsageWindow {
  tenantId: string | null;
  from: string;
  to: string;
  calls: number;
  rows: number;
  ms: number;
  byOutcome: OutcomeCounts;
  byTool: ToolAggregate[];
  /** One point per day in the window, zero-filled by the server. */
  series: SeriesPoint[];
}

export interface FleetTenant {
  tenantId: string;
  displayName: string;
  origin: string;
  ownership: Policy['ownership'] | string;
  tools: number;
  verdict: Verdict;
  lastCheckedAt: string | null;
  calls: number;
  /** Capabilities whose binding advertises a parameter it cannot honour. */
  issues: number;
}

/**
 * A manifest on disk the registry could not load.
 *
 * The tenant is dark — `/t/<id>` and `/t/<id>/mcp` answer nothing — and until
 * this row existed nothing anywhere said so. It is the first block on the fleet
 * screen for that reason.
 */
export interface FailedTenant {
  tenantId: string;
  path: string | null;
  error: string;
  at: string | null;
}

export interface Overview {
  tenants: FleetTenant[];
  failed: FailedTenant[];
  usage: UsageWindow;
}

export interface ToolHealth {
  tool: string;
  state: 'ok' | 'empty' | 'error' | 'unknown';
  rows: number;
  error: string | null;
  checkedAt: string | null;
  consecutiveFailures: number;
  rotten: boolean;
}

export interface TenantHealth {
  tenantId: string;
  verdict: Verdict;
  lastCheckedAt: string | null;
  tools: ToolHealth[];
}

/**
 * A domain claim, as a screen has to state it.
 *
 * Everything past `method` is optional because ONE component renders this on two
 * surfaces: the site screen reads it out of the tenant payload, where the server
 * fills every field, and the publish gate on the public console reads it out of
 * `GET /api/ownership/:tenant`, which answers a narrower claim. Making the extras
 * optional is what lets the two callers share the component instead of the
 * component being written twice and drifting — which is how the dashboard ended
 * up with no proofs at all.
 *
 * `state` is never the whole answer. A claim can read `verified` and be expired,
 * or be about an origin the tenant no longer serves; `current` is the field that
 * agrees with what `/publish` will do.
 */
export interface OwnershipRecord {
  state: string;
  domain: string | null;
  method: string | null;
  verifiedAt?: string | null;
  expiresAt?: string | null;
  current?: boolean;
  daysUntilExpiry?: number | null;
  renewDue?: boolean;
  lastCheckOk?: boolean;
  /** The verifier's own words for the last check. A miss says what it saw. */
  lastCheckDetail?: string | null;
  /** Non-empty only when the claim has drifted off the origin the tenant serves. */
  originDrift?: string | null;
  policyApplied?: boolean;
  revokedReason?: string | null;
}

/**
 * A challenge issued and not yet answered.
 *
 * Its token was shown once, on the response that issued it, and is not here — so
 * an open challenge is something to VERIFY against, never something to display.
 * `tokenHint` is a fragment, enough for a person holding the string they were
 * shown to tell whether it is still the one this challenge expects.
 */
export interface OpenChallengeRecord {
  id: string;
  domain: string;
  expiresAt: string | null;
  attempts: number;
  tokenHint?: string | null;
}

/**
 * `POST /api/ownership/:tenant/challenge` — the three proofs, with the token in
 * them, returned exactly once.
 *
 * Read verbatim from the API rather than restated: the verifier checks against
 * what this response said, and a console that rewrote the instructions in its
 * own words would hand a person two procedures to reconcile at a registrar's
 * control panel.
 */
export interface ProofChallenge {
  challengeId: string;
  domain: string;
  instructions: {
    dns: { recordName: string; recordType: string; value: string; howTo: string };
    file: { url: string; path: string; body: string; howTo: string };
    meta: { url: string; metaName: string; tag: string; howTo: string };
  };
  next: {
    verifyUrl: string;
    statusUrl: string;
    propagation: string;
    answerBy: string;
    tokenShownOnce: string;
  };
  note?: string;
}

export interface ProofAttempt {
  method: string;
  ok: boolean;
  code: string;
  detail: string;
}

/**
 * `POST …/verify` and `POST …/recheck`. A miss is a 200 with `verified: false`
 * and an attempts list saying exactly what the verifier saw, which is the only
 * useful thing a failed domain check can produce.
 */
export interface ProofVerification {
  verified: boolean;
  method: string | null;
  attempts: ProofAttempt[];
  /** The API's own "what to do next" sentence, on a miss. */
  nextStep?: string;
  /** On a hit: what changed, with the API's reminder that nothing was published. */
  effect?: { note: string };
}

/** `GET /api/ownership/:tenant` — is there a claim, and is a challenge open. */
export interface OwnershipStatus {
  claim: OwnershipRecord | null;
  openChallenge: OpenChallengeRecord | null;
}

export interface TenantDetail {
  tenantId: string;
  identity: Manifest['identity'];
  brand: Brand;
  policy: Policy;
  source: Manifest['source'];
  capabilities: Capability[];
  views: View[];
  usage: UsageWindow;
  health: TenantHealth;
  ownership: OwnershipRecord | null;
  /** A challenge already issued for this site and not yet answered. */
  openChallenge: OpenChallengeRecord | null;
  /** Tool name → binding-variable messages, as the server reported them. */
  issues: Record<string, string[]>;
  urls: TenantUrls;
  /**
   * Hostnames declared to forward `/mcp` here. One of the two keys the MCP
   * route checks before it answers a browser on the tenant's own domain; the
   * other is `ownership`. Empty on a server too old to send it, which the
   * Connect tab renders as "none declared", the truth about that server.
   */
  proxyHosts: string[];
}

export interface Freshness {
  state: string;
  ageSec: number | null;
  ttlSec: number | null;
}

export interface RunResult {
  ok: boolean;
  rows: Array<Record<string, unknown>>;
  count: number;
  freshness: Freshness | null;
  notes: string[];
  ms: number;
  /** Present when the tool ran and failed. Rendered as the error, never as an empty table. */
  error: string | null;
}

export interface ToolPatch {
  title?: string;
  description?: string;
  web?: boolean;
  app?: boolean;
  visibility?: Array<'model' | 'app'>;
  ttlSec?: number;
  /** Whether the widget's Send button sends this tool's form through the server. */
  relay?: boolean;
}

export type ViewPatch = Partial<Pick<View, 'layout' | 'title' | 'slots' | 'badges' | 'linkField'>>;

/**
 * Who this page is acting as, as `GET /api/auth/me` reports it.
 *
 * The operator is the reload-secret holder and sees every tenant on the host.
 * An account is a person who signed in by email and sees only the tenants it is
 * a member of — the SERVER draws that line, and the screens render whatever the
 * scoped routes return without filtering it again.
 */
export type Principal =
  | { kind: 'operator' }
  | {
      kind: 'account';
      accountId: string;
      email: string;
      tenants: Array<{ tenantId: string; role: string }>;
    };

/** `POST /api/auth/login`. `sent` is true for every well-formed address, on purpose. */
export interface LoginReceipt {
  sent: boolean;
  /**
   * Present only under `CLAWHARBOR_EMAIL_PROVIDER=log`: the link that would have
   * been emailed. Rendered plainly labelled as development mode, because it is
   * how an operator proves login works before a provider key exists.
   */
  devLink: string | null;
}

/**
 * `GET /api/auth/2fa`: whether this account asks for a code after the link.
 *
 * `recoveryRemaining` is a count, never the codes: those are shown once, at
 * enrolment or regeneration, and the server keeps only their hashes.
 */
export interface TwoFactorStatus {
  enabled: boolean;
  confirmedAt: string | null;
  recoveryRemaining: number;
}

/** `POST /api/auth/2fa/setup`: an unconfirmed secret, to be typed or tapped into an app. */
export interface TwoFactorEnrolment {
  secret: string;
  otpauthUri: string;
}

/** The public URLs the server publishes a tenant at. Never rebuilt from a constant. */
export interface TenantUrls {
  page: string;
  mcp: string;
  manifest: string;
  tools: string;
  ui: string;
  /** `/t/<id>/embed.js` — the WebMCP tag for the tenant's own site. */
  embedJs: string;
  /** `/t/<id>/embed` — the page that explains the tag. */
  embed: string;
  /** `/t/<id>/icon.svg`, which a proxy may serve at `/mcp/icon.svg`. */
  icon: string;
  /** The RFC 9728 location a proxy keeps reachable under its own domain. */
  protectedResourceMetadata: string;
  /** `/t/<id>/preview` — the widget index, each view inside a simulated host. */
  preview: string;
}

export interface BillingPlan {
  id: string;
  name: string;
  /** Per month. `null` is a plan with no stated cap (talk to us), never "zero". */
  callsIncluded: number | null;
  crawls: number | null;
  /**
   * Whatever the server chose to say about the price, verbatim. This client
   * never formats a number the API did not send: a price rendered from a
   * constant is a price that can disagree with the one Stripe charges.
   */
  price: string | null;
}

export interface Billing {
  /** False while no Stripe key is set. The screen says so, and the buttons are dead. */
  configured: boolean;
  plan: BillingPlan;
  subscription: { status: string; currentPeriodEnd: string | null } | null;
  usage: { used: number; remaining: number | null };
  /**
   * The plans a checkout may be started for, when the server lists them. The
   * upgrade buttons are drawn from this list; with none sent, the screen offers
   * the two ids the checkout route accepts and no more.
   */
  plans: BillingPlan[];
}

/* ── Onboarding ────────────────────────────────────────────────────────── */

/** apps/server/src/onboard/store.ts. A job is in exactly one of these. */
export const JOB_STATES = ['queued', 'running', 'succeeded', 'failed', 'cancelled'] as const;
export type JobState = (typeof JOB_STATES)[number];

/**
 * What the submitter asserts about their standing on `POST /api/onboard`.
 *
 * `self-owned` and `permitted` drop the third-party disclaimer from the
 * manifest the crawler assembles, which is why the server treats them as claims
 * to be proved at publish time rather than as rendering flags. The screen
 * offers all three and says what each one costs; the server decides.
 */
export type Ownership = 'self-owned' | 'third-party' | 'permitted';

/** `GET /api/onboard/:id` — the public job record, as `publicJob` shapes it. */
export interface OnboardJob {
  id: string;
  url: string;
  tenantId: string;
  state: JobState;
  phase: string;
  /** 0–100, derived from the crawler's stage banners. Coarse by nature. */
  progress: number;
  ownership: Ownership | string;
  /** Why it failed, when it did. Null otherwise. */
  error: string | null;
  /** How many capabilities the draft carries, once the crawl has finished. */
  capabilities: number | null;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  publishedAt: string | null;
  /**
   * Succeeded and not published. The server's own word for it, and the field
   * the screen keys its wording on: a draft is never live.
   */
  draft: boolean;
  log: string[];
}

/** `POST /api/onboard`. A 200 with `deduplicated` joins a crawl already running. */
export interface OnboardReceipt {
  jobId: string;
  tenantId: string;
  state: JobState;
  deduplicated: boolean;
  queuePosition: number;
  /** The route's own reminder that a crawl produces a draft. Shown as sent. */
  note: string | null;
}

/**
 * The arithmetic that decided whether a tool ships switched on.
 *
 * The crawl over-produces: nine tools came off one utility's site and its
 * operator kept three. Rather than delete the other six the crawl now ships
 * them with `surfaces.*.register` false and records why, so the judgement — a
 * heuristic, and heuristics are wrong sometimes — can be read and reversed by a
 * person instead of guessed at. `@clawharbor/manifest` calls this `usefulness`
 * and this is the same object, carried through unchanged.
 */
export interface DraftUsefulness {
  score: number;
  /** The score at or above which this run registered a tool. */
  threshold: number;
  reasons: Array<{ signal: string; delta: number; note: string }>;
}

export interface DraftCapability {
  name: string;
  kind: string;
  title: string;
  description: string;
  view: string | null;
  /**
   * Whether the tool ships switched on. False is the crawl's own judgement,
   * not a failure — and the reason a draft can list twelve tools while the
   * published site answers with three.
   */
  register: boolean;
  /** Null on a manifest written before the crawl scored anything, and by hand. */
  usefulness: DraftUsefulness | null;
}

/** `GET /api/onboard/:id/manifest`, reduced to the `summary` the review needs. */
export interface DraftSummary {
  jobId: string;
  tenantId: string;
  draft: boolean;
  displayName: string;
  origin: string;
  ownership: string;
  capabilities: DraftCapability[];
  views: string[];
}

/**
 * What a re-publish did to the edits already on the live tenant.
 *
 * `mergeOperatorEdits` carries dashboard decisions across a re-crawl — tool
 * titles, descriptions, surface switches, freshness, the form relay, view slots
 * and custom CSS — and names every one it could not place. Both lists are empty
 * on a first publish, and an empty `carried` after a replacement is itself the
 * answer: nothing had been edited, so nothing was at risk.
 */
export interface MergeReport {
  /** e.g. `tool "list_screenings": title, description`. */
  carried: string[];
  /** Edits with nowhere to land, because the tool or view is gone. */
  dropped: string[];
}

/** `POST /api/onboard/:id/publish`. The URLs are the server's; nothing is rebuilt here. */
export interface PublishReceipt {
  tenantId: string;
  displayName: string | null;
  ownership: string | null;
  capabilities: string[];
  /** A tenant of the same id was already live and has been replaced. */
  replaced: boolean;
  /**
   * The merge, in the operator's words. Kept even when both lists are empty:
   * "nothing was carried" is a sentence the receipt has to be able to say, and
   * a missing field would make it indistinguishable from a server too old to
   * report one.
   */
  edits: MergeReport;
  urls: { web: string; mcp: string };
}

/* ── Readers ───────────────────────────────────────────────────────────── */

/** "The server answered in a shape this screen cannot render", with the path. */
export class ContractError extends Error {
  readonly path: string;
  constructor(path: string, expected: string, got: unknown) {
    super(`${path}: expected ${expected}, got ${describe(got)}`);
    this.name = 'ContractError';
    this.path = path;
  }
}

/** "The server answered and said no" — a real answer, not an outage. */
export class ApiError extends Error {
  readonly status: number;
  /** The server's own `code`, e.g. `billing_disabled`. Empty when it sent none. */
  readonly code: string;
  constructor(message: string, status: number, code = '') {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

function describe(value: unknown): string {
  if (value === undefined) return 'nothing (the field is absent)';
  if (value === null) return 'null';
  if (Array.isArray(value)) return `an array of ${value.length}`;
  if (typeof value === 'object') {
    const keys = Object.keys(value as object);
    return `an object with keys [${keys.slice(0, 8).join(', ')}]`;
  }
  return `${typeof value} ${JSON.stringify(value)}`;
}

function obj(value: unknown, path: string): Record<string, unknown> {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) {
    throw new ContractError(path, 'an object', value);
  }
  return value as Record<string, unknown>;
}

function arr(value: unknown, path: string): unknown[] {
  if (!Array.isArray(value)) throw new ContractError(path, 'an array', value);
  return value;
}

function str(value: unknown, path: string): string {
  if (typeof value !== 'string') throw new ContractError(path, 'a string', value);
  return value;
}

/** A missing string that the screen is allowed to render as an absence. */
function optStr(value: unknown): string | null {
  return typeof value === 'string' && value ? value : null;
}

function optNum(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function optBool(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

/**
 * The first name present, and the key it was found under.
 *
 * Field names on a route written in parallel with this client are the one thing
 * likely to differ, and accepting the two or three spellings a reasonable
 * implementer lands on costs nothing. What is NOT tolerated is all of them
 * missing: that raises through the caller's `ContractError` rather than
 * defaulting, because a silently defaulted count is a number an operator will
 * believe.
 */
function alias(o: Record<string, unknown>, names: readonly string[]): unknown {
  for (const n of names) {
    if (o[n] !== undefined && o[n] !== null) return o[n];
  }
  return undefined;
}

function counts(value: unknown): OutcomeCounts {
  const src = value == null || typeof value !== 'object' ? {} : (value as Record<string, unknown>);
  const out = {} as OutcomeCounts;
  for (const k of OUTCOMES) out[k] = optNum(src[k]);
  return out;
}

function verdict(value: unknown): Verdict {
  const v = typeof value === 'string' ? value : '';
  if (v === 'healthy' || v === 'degraded' || v === 'broken') return v;
  // Anything else is "we do not know", which the fleet renders as "never
  // checked" rather than as a green tick.
  return 'unknown';
}

function messages(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.map((m) => (typeof m === 'string' ? m : JSON.stringify(m))).filter(Boolean);
}

/**
 * A timestamp that may arrive as an ISO string or as epoch milliseconds.
 *
 * The usage store holds `last_at` as a number and the dashboard service maps it
 * to ISO on the way out; a route that ever forwards the store row untouched
 * would otherwise render "1774..." as a date. Zero and the epoch are read as
 * "never", because a tool that has not been called in the window has no last
 * call and 1 January 1970 is a bug wearing a date's clothes.
 */
function isoOf(value: unknown): string | null {
  if (typeof value === 'string') return value.trim() ? value : null;
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return new Date(value).toISOString();
  }
  return null;
}

function toolAggregate(raw: unknown, path: string): ToolAggregate {
  const o = obj(raw, path);
  return {
    tenantId: optStr(alias(o, ['tenantId', 'tenant', 'id'])),
    tool: str(alias(o, ['tool', 'name']), `${path}.tool`),
    calls: optNum(alias(o, ['calls', 'count'])),
    rows: optNum(o['rows']),
    ms: optNum(o['ms']),
    byOutcome: counts(alias(o, ['byOutcome', 'outcomes'])),
    lastCalledAt: isoOf(alias(o, ['lastCalledAt', 'lastAt', 'last'])),
  };
}

function seriesPoint(raw: unknown, path: string): SeriesPoint {
  const o = obj(raw, path);
  return {
    day: str(alias(o, ['day', 'date']), `${path}.day`),
    calls: optNum(alias(o, ['calls', 'count'])),
    rows: optNum(o['rows']),
    ms: optNum(o['ms']),
    byOutcome: counts(alias(o, ['byOutcome', 'outcomes'])),
  };
}

function usageWindow(raw: unknown, path: string): UsageWindow {
  const o = obj(raw, path);
  const series = arr(alias(o, ['series', 'days', 'byDay']) ?? [], `${path}.series`).map((p, i) =>
    seriesPoint(p, `${path}.series[${i}]`),
  );
  const byTool = arr(alias(o, ['byTool', 'tools']) ?? [], `${path}.byTool`).map((t, i) =>
    toolAggregate(t, `${path}.byTool[${i}]`),
  );
  const byOutcome = counts(alias(o, ['byOutcome', 'outcomes']));
  // Totals are derived from the series when the server sends only the series,
  // and never invented: with no series and no total this is a zero, which is
  // what a host that has served no calls should read as.
  const summed = series.reduce((n, p) => n + p.calls, 0);
  const declared = alias(o, ['calls', 'count', 'total']);
  return {
    tenantId: optStr(o['tenantId']),
    from: optStr(alias(o, ['from', 'since'])) ?? (series[0]?.day ?? ''),
    to: optStr(alias(o, ['to', 'until'])) ?? (series[series.length - 1]?.day ?? ''),
    calls: typeof declared === 'number' ? declared : summed,
    rows: optNum(o['rows']),
    ms: optNum(o['ms']),
    byOutcome,
    byTool,
    series,
  };
}

function toolHealth(raw: unknown, path: string): ToolHealth {
  const o = obj(raw, path);
  const state = typeof o['state'] === 'string' ? o['state'] : 'unknown';
  return {
    tool: str(alias(o, ['tool', 'name']), `${path}.tool`),
    state: state === 'ok' || state === 'empty' || state === 'error' ? state : 'unknown',
    rows: optNum(o['rows']),
    error: optStr(o['error']),
    checkedAt: optStr(alias(o, ['checkedAt', 'at', 'lastCheckedAt'])),
    consecutiveFailures: optNum(o['consecutiveFailures']),
    rotten: optBool(o['rotten'], false),
  };
}

export function readTenantHealth(raw: unknown, path = 'health'): TenantHealth {
  const o = obj(raw, path);
  // A single-tenant health route may answer `{ok, status:{…}}`.
  const inner = o['status'] ?? o['health'] ?? o;
  const h = obj(inner, path);
  return {
    tenantId: optStr(h['tenantId']) ?? '',
    verdict: verdict(h['verdict']),
    lastCheckedAt: optStr(alias(h, ['lastCheckedAt', 'checkedAt', 'at'])),
    tools: arr(h['tools'] ?? [], `${path}.tools`).map((t, i) => toolHealth(t, `${path}.tools[${i}]`)),
  };
}

function fleetTenant(raw: unknown, path: string): FleetTenant {
  const o = obj(raw, path);
  const health = o['health'] != null && typeof o['health'] === 'object'
    ? (o['health'] as Record<string, unknown>)
    : o;
  const usage = o['usage'] != null && typeof o['usage'] === 'object'
    ? (o['usage'] as Record<string, unknown>)
    : o;
  const caps = alias(o, ['tools', 'toolCount', 'capabilities']);
  return {
    tenantId: str(alias(o, ['tenantId', 'id']), `${path}.tenantId`),
    displayName: optStr(alias(o, ['displayName', 'name'])) ?? str(alias(o, ['tenantId', 'id']), `${path}.tenantId`),
    origin: optStr(alias(o, ['origin', 'canonicalOrigin', 'url'])) ?? '',
    ownership: optStr(o['ownership']) ?? 'unknown',
    tools: Array.isArray(caps) ? caps.length : optNum(caps),
    verdict: verdict(alias(health, ['verdict', 'health'])),
    lastCheckedAt: optStr(alias(health, ['lastCheckedAt', 'checkedAt'])),
    // `calls7` is the name the server sends. The others are kept as fallbacks,
    // but the contract test is what makes this column trustworthy: reading a
    // name nothing answers to renders a confident 0 next to a busy tenant, which
    // is the one thing a usage column must never do.
    calls: optNum(alias(usage, ['calls7', 'calls', 'calls7d', 'count'])),
    issues: optNum(alias(o, ['issues', 'bindingIssues', 'issueCount'])),
  };
}

function failedTenant(raw: unknown, path: string): FailedTenant {
  const o = obj(raw, path);
  return {
    tenantId: optStr(alias(o, ['tenantId', 'id'])) ?? '(unnamed)',
    path: optStr(alias(o, ['path', 'file'])),
    error: optStr(alias(o, ['error', 'message', 'reason'])) ?? 'the manifest failed to load',
    at: optStr(alias(o, ['at', 'failedAt'])),
  };
}

/**
 * A missing list is a moved contract, not an empty fleet.
 *
 * `?? []` over a guessed field name is how this screen reported "0 tenants on
 * this host" against an API that was returning one: the server names the list
 * `loaded`, the parser looked for `tenants` and `fleet`, and the default made the
 * mismatch indistinguishable from the truth. An empty array still means empty —
 * an absent key means this build and that server disagree, and the operator has
 * to be told rather than reassured.
 */
function requiredList(o: Record<string, unknown>, names: readonly string[], path: string): unknown[] {
  const value = alias(o, names);
  if (value === undefined) {
    throw new Error(
      `${path}: the response carries none of ${names.join(', ')} — this console and the server disagree about the shape`,
    );
  }
  return arr(value, path);
}

export function readOverview(raw: unknown): Overview {
  const o = obj(raw, 'overview');
  return {
    tenants: requiredList(o, ['loaded', 'tenants', 'fleet'], 'overview.loaded').map((t, i) =>
      fleetTenant(t, `overview.loaded[${i}]`),
    ),
    failed: requiredList(o, ['failed', 'failures', 'dark'], 'overview.failed').map((t, i) =>
      failedTenant(t, `overview.failed[${i}]`),
    ),
    usage: usageWindow(o['usage'] ?? {}, 'overview.usage'),
  };
}

/**
 * Capabilities and views are carried through as the manifest types they are.
 *
 * They are validated only for the fields the cards address by name: a card that
 * reads `cap.inputSchema.properties` has to fail loudly on a capability with no
 * `name`, and has to survive a manifest field this build has never heard of,
 * because the tenant on disk may have been written by a newer generator.
 */
function capability(raw: unknown, path: string): Capability {
  const o = obj(raw, path);
  str(alias(o, ['name', 'id']), `${path}.name`);
  if (o['inputSchema'] != null) obj(o['inputSchema'], `${path}.inputSchema`);
  return o as unknown as Capability;
}

function view(raw: unknown, path: string): View {
  const o = obj(raw, path);
  str(o['id'], `${path}.id`);
  return o as unknown as View;
}

function ownership(raw: unknown): OwnershipRecord | null {
  if (raw == null || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const state = optStr(alias(o, ['state', 'status']));
  const verifiedAt = optStr(alias(o, ['verifiedAt', 'at']));
  if (!state && !verifiedAt && !o['domain']) return null;
  const days = alias(o, ['daysUntilExpiry', 'days']);
  return {
    state: state ?? (verifiedAt ? 'verified' : 'unverified'),
    domain: optStr(o['domain']),
    method: optStr(o['method']),
    verifiedAt,
    expiresAt: optStr(o['expiresAt']),
    /**
     * Defaulted to FALSE, never to "well, it says verified".
     *
     * A claim's state is a stored word and its currency is a live derivation; a
     * reader that inferred one from the other would tell a person their domain
     * is proven on exactly the hosts old enough not to send the field, which is
     * the population most likely to have an expired claim.
     */
    current: optBool(o['current'], false),
    daysUntilExpiry: typeof days === 'number' ? days : null,
    renewDue: optBool(o['renewDue'], false),
    lastCheckOk: optBool(o['lastCheckOk'], false),
    lastCheckDetail: optStr(o['lastCheckDetail']),
    originDrift: optStr(o['originDrift']),
    policyApplied: optBool(o['policyApplied'], false),
    revokedReason: optStr(o['revokedReason']),
  };
}

function openChallenge(raw: unknown): OpenChallengeRecord | null {
  if (raw == null || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const id = optStr(alias(o, ['id', 'challengeId']));
  if (!id) return null;
  return {
    id,
    domain: optStr(o['domain']) ?? '',
    expiresAt: optStr(o['expiresAt']),
    attempts: optNum(o['attempts']),
    tokenHint: optStr(o['tokenHint']),
  };
}

/**
 * The three proofs, as the challenge response worded them.
 *
 * Every field is required and every one throws when it is missing, unlike most
 * of this file: a half-read proof block is a person publishing a DNS record with
 * an empty value and being told, correctly and uselessly, that no TXT record was
 * found. There is no sensible default for "the string that proves you own this".
 */
export function readOwnershipChallenge(raw: unknown): ProofChallenge {
  const outer = obj(raw, 'challenge');
  const o = obj(outer['challenge'] ?? outer, 'challenge');
  const ins = obj(o['instructions'], 'challenge.instructions');
  const dns = obj(ins['dns'], 'challenge.instructions.dns');
  const file = obj(ins['file'], 'challenge.instructions.file');
  const meta = obj(ins['meta'], 'challenge.instructions.meta');
  const next = obj(o['next'], 'challenge.next');
  const note = optStr(o['note']);
  return {
    challengeId: str(alias(o, ['challengeId', 'id']), 'challenge.challengeId'),
    domain: str(o['domain'], 'challenge.domain'),
    instructions: {
      dns: {
        recordName: str(dns['recordName'], 'challenge.instructions.dns.recordName'),
        recordType: str(dns['recordType'], 'challenge.instructions.dns.recordType'),
        value: str(dns['value'], 'challenge.instructions.dns.value'),
        howTo: str(dns['howTo'], 'challenge.instructions.dns.howTo'),
      },
      file: {
        url: str(file['url'], 'challenge.instructions.file.url'),
        path: str(file['path'], 'challenge.instructions.file.path'),
        body: str(file['body'], 'challenge.instructions.file.body'),
        howTo: str(file['howTo'], 'challenge.instructions.file.howTo'),
      },
      meta: {
        url: str(meta['url'], 'challenge.instructions.meta.url'),
        metaName: str(meta['metaName'], 'challenge.instructions.meta.metaName'),
        tag: str(meta['tag'], 'challenge.instructions.meta.tag'),
        howTo: str(meta['howTo'], 'challenge.instructions.meta.howTo'),
      },
    },
    next: {
      verifyUrl: str(next['verifyUrl'], 'challenge.next.verifyUrl'),
      statusUrl: str(next['statusUrl'], 'challenge.next.statusUrl'),
      propagation: str(next['propagation'], 'challenge.next.propagation'),
      answerBy: str(next['answerBy'], 'challenge.next.answerBy'),
      tokenShownOnce: str(next['tokenShownOnce'], 'challenge.next.tokenShownOnce'),
    },
    ...(note ? { note } : {}),
  };
}

/**
 * A verification or a re-check, which answer in the same shape.
 *
 * `verified` defaults to false and the attempts list defaults to empty, so a
 * response this build has never seen reads as "not yet, and here is nothing" —
 * a screen that says try again — rather than as a proven domain.
 */
export function readOwnershipVerification(raw: unknown): ProofVerification {
  const o = obj(raw, 'verification');
  const attempts = Array.isArray(o['attempts']) ? (o['attempts'] as unknown[]) : [];
  const nextStep = optStr(o['nextStep']);
  const effect = o['effect'] != null && typeof o['effect'] === 'object'
    ? optStr((o['effect'] as Record<string, unknown>)['note'])
    : null;
  return {
    verified: optBool(o['verified'], false),
    method: optStr(o['method']),
    attempts: attempts.flatMap((a) => {
      if (a == null || typeof a !== 'object') return [];
      const e = a as Record<string, unknown>;
      return [
        {
          method: optStr(e['method']) ?? 'unknown',
          ok: optBool(e['ok'], false),
          code: optStr(e['code']) ?? 'unknown',
          detail: optStr(e['detail']) ?? '',
        },
      ];
    }),
    ...(nextStep ? { nextStep } : {}),
    ...(effect ? { effect: { note: effect } } : {}),
  };
}

/** `GET /api/ownership/:tenant`, read for the two facts a panel branches on. */
export function readOwnershipStatus(raw: unknown): OwnershipStatus {
  const o = obj(raw, 'ownership');
  return {
    claim: ownership(o['claim']),
    openChallenge: openChallenge(o['openChallenge']),
  };
}

/**
 * The binding-variable issues, keyed by tool name.
 *
 * Accepted three ways because this is the field most likely to move: a map on
 * the response, a list of `{tool, issues}`, or the array hung on each
 * capability. A capability that advertises a parameter its binding never reads
 * answers every value of that parameter identically — the live tenant answered
 * "when is Stalker showing?" with Vertigo's showtime — so this is never allowed
 * to depend on one spelling being right.
 */
function issues(o: Record<string, unknown>, caps: Capability[]): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  const add = (tool: string, list: string[]): void => {
    if (!list.length) return;
    out[tool] = [...(out[tool] ?? []), ...list];
  };

  const raw = alias(o, ['issues', 'bindingIssues', 'bindingVarIssues']);
  if (Array.isArray(raw)) {
    for (const entry of raw) {
      if (entry == null || typeof entry !== 'object') continue;
      const e = entry as Record<string, unknown>;
      const tool = optStr(alias(e, ['tool', 'name', 'capability']));
      if (tool) add(tool, messages(alias(e, ['issues', 'messages'])));
    }
  } else if (raw != null && typeof raw === 'object') {
    for (const [tool, list] of Object.entries(raw as Record<string, unknown>)) {
      add(tool, messages(list));
    }
  }

  for (const cap of caps) {
    const c = cap as unknown as Record<string, unknown>;
    add(cap.name, messages(alias(c, ['bindingVarIssues', 'issues'])));
  }
  return out;
}

export function readTenantDetail(raw: unknown, fallbackId: string): TenantDetail {
  const outer = obj(raw, 'tenant');
  // A guarded route in this repo answers `{ok:true, …}`; some answer `{tenant:{…}}`.
  const o = obj(outer['tenant'] ?? outer, 'tenant');
  const caps = arr(alias(o, ['capabilities', 'tools']) ?? [], 'tenant.capabilities').map((c, i) =>
    capability(c, `tenant.capabilities[${i}]`),
  );
  return {
    tenantId: optStr(alias(o, ['tenantId', 'id'])) ?? fallbackId,
    identity: obj(o['identity'], 'tenant.identity') as unknown as Manifest['identity'],
    brand: obj(o['brand'], 'tenant.brand') as unknown as Brand,
    policy: obj(o['policy'], 'tenant.policy') as unknown as Policy,
    source: obj(o['source'], 'tenant.source') as unknown as Manifest['source'],
    capabilities: caps,
    views: arr(o['views'] ?? [], 'tenant.views').map((v, i) => view(v, `tenant.views[${i}]`)),
    usage: usageWindow(o['usage'] ?? {}, 'tenant.usage'),
    health: readTenantHealth(o['health'] ?? {}, 'tenant.health'),
    // `claim` is the name the server actually sends (DashboardService's
    // TenantDetail.claim); `ownership` was the name this reader was written
    // against and nothing has ever sent it, so the tenant header printed "no
    // ownership claim on record" for every tenant on every host, always.
    ownership: ownership(alias(o, ['ownership', 'claim'])),
    openChallenge: openChallenge(alias(o, ['openChallenge', 'challenge'])),
    issues: issues(o, caps),
    urls: readTenantUrls(o['urls']),
    proxyHosts: (() => {
      const integration = o['integration'];
      if (integration == null || typeof integration !== 'object') return [];
      const hosts = (integration as Record<string, unknown>)['proxyHosts'];
      return Array.isArray(hosts) ? hosts.filter((h): h is string => typeof h === 'string') : [];
    })(),
  };
}

export function readUsage(raw: unknown): UsageWindow {
  const o = obj(raw, 'usage');
  return usageWindow(o['usage'] ?? o, 'usage');
}

function freshness(raw: unknown): Freshness | null {
  if (raw == null) return null;
  if (typeof raw === 'string') return { state: raw, ageSec: null, ttlSec: null };
  if (typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const state = optStr(alias(o, ['state', 'status']));
  if (!state) return null;
  const age = alias(o, ['ageSec', 'age']);
  const ttl = alias(o, ['ttlSec', 'ttl']);
  return {
    state,
    ageSec: typeof age === 'number' ? age : null,
    ttlSec: typeof ttl === 'number' ? ttl : null,
  };
}

/**
 * "Run it" — the real returned rows.
 *
 * An error is read out of the body and returned as `error`, never dropped: the
 * whole value of this control is that it distinguishes a claimed tool from a
 * demonstrated one, and an empty table where a stack trace belongs would make
 * a broken tool look merely unpopular.
 */
export function readRunResult(raw: unknown, ms: number): RunResult {
  const o = obj(raw, 'run');
  /**
   * The route answers `{ok, test:{…}}`. Reading only `result` left `inner`
   * pointing at the envelope, where there are no rows — so the one control that
   * exists to turn "1 sample · 100% pass" into rows an operator can read would
   * have rendered an empty table for a tool that had just returned two.
   */
  const wrapped = o['test'] ?? o['result'];
  const inner = wrapped != null && typeof wrapped === 'object'
    ? (wrapped as Record<string, unknown>)
    : o;
  const rowsRaw = alias(inner, ['rows', 'data', 'items']);
  const rows = Array.isArray(rowsRaw)
    ? rowsRaw.map((r) => (r != null && typeof r === 'object' ? (r as Record<string, unknown>) : { value: r }))
    : [];
  const error = optStr(alias(o, ['error', 'message'])) ?? optStr(inner['error']);
  const declaredMs = alias(o, ['ms', 'elapsedMs']) ?? alias(inner, ['ms', 'elapsedMs']);
  const count = alias(inner, ['count', 'total']);
  return {
    ok: optBool(alias(o, ['ok']), error == null),
    rows,
    count: typeof count === 'number' ? count : rows.length,
    freshness: freshness(alias(inner, ['freshness', 'fresh'])),
    notes: messages(alias(inner, ['notes', 'warnings'])),
    ms: typeof declaredMs === 'number' ? declaredMs : ms,
    error,
  };
}

function num(value: unknown, path: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) throw new ContractError(path, 'a number', value);
  return value;
}

function bool(value: unknown, path: string): boolean {
  if (typeof value !== 'boolean') throw new ContractError(path, 'a boolean', value);
  return value;
}

/** A number the server may honestly report as "no cap": present, and a number or null. */
function numOrNull(value: unknown, path: string): number | null {
  if (value === null) return null;
  return num(value, path);
}

/**
 * `GET /api/auth/me`.
 *
 * Nothing here defaults. A principal read as `{kind:'account'}` with an absent
 * `tenants` would render "no tenant yet — crawl your site to get one" to a
 * person who owns three, which is the "0 tenants on this host" defect the fleet
 * reader already had once; the kind is checked against the two values the
 * contract names because any third spelling is a server this build does not know.
 */
export function readPrincipal(raw: unknown): Principal {
  const outer = obj(raw, 'me');
  const o = obj(outer['principal'], 'me.principal');
  const kind = str(o['kind'], 'me.principal.kind');
  if (kind === 'operator') return { kind: 'operator' };
  if (kind !== 'account') {
    throw new ContractError('me.principal.kind', "'operator' or 'account'", kind);
  }
  return {
    kind: 'account',
    accountId: str(o['accountId'], 'me.principal.accountId'),
    email: str(o['email'], 'me.principal.email'),
    tenants: arr(o['tenants'], 'me.principal.tenants').map((t, i) => {
      const m = obj(t, `me.principal.tenants[${i}]`);
      return {
        tenantId: str(m['tenantId'], `me.principal.tenants[${i}].tenantId`),
        role: str(m['role'], `me.principal.tenants[${i}].role`),
      };
    }),
  };
}

export function readTwoFactorStatus(raw: unknown): TwoFactorStatus {
  const o = obj(raw, '2fa');
  return {
    // Required: "off" must be the server's word. A reader that defaulted a
    // missing flag to false would tell an enrolled person their account has no
    // second factor, which is the one thing this panel must never get wrong.
    enabled: bool(o['enabled'], '2fa.enabled'),
    confirmedAt: optStr(o['confirmedAt']),
    recoveryRemaining: optNum(o['recoveryRemaining']),
  };
}

export function readTwoFactorEnrolment(raw: unknown): TwoFactorEnrolment {
  const o = obj(raw, '2fa.setup');
  return {
    secret: str(o['secret'], '2fa.setup.secret'),
    otpauthUri: str(o['otpauthUri'], '2fa.setup.otpauthUri'),
  };
}

export function readRecoveryCodes(raw: unknown, path: string): string[] {
  const o = obj(raw, path);
  return arr(o['recoveryCodes'], `${path}.recoveryCodes`).map((c, i) => str(c, `${path}.recoveryCodes[${i}]`));
}

export function readLoginReceipt(raw: unknown): LoginReceipt {
  const o = obj(raw, 'login');
  return {
    sent: bool(o['sent'], 'login.sent'),
    devLink: optStr(o['devLink']),
  };
}

/**
 * The tenant's public URLs, as the server names them.
 *
 * `embedJs` and `embed` are derived from `page` when the server does not list
 * them, which is still the SERVER's host: the embed snippet must never be built
 * from a constant, because a host behind a proxy with a different public name
 * would hand every tenant a tag that points at the wrong origin.
 */
export function readTenantUrls(raw: unknown, path = 'tenant.urls'): TenantUrls {
  const o = obj(raw, path);
  const page = str(o['page'], `${path}.page`);
  return {
    page,
    mcp: str(o['mcp'], `${path}.mcp`),
    manifest: str(o['manifest'], `${path}.manifest`),
    tools: str(o['tools'], `${path}.tools`),
    ui: str(o['ui'], `${path}.ui`),
    embedJs: optStr(alias(o, ['embedJs', 'embedScript'])) ?? `${page}/embed.js`,
    embed: optStr(alias(o, ['embed', 'embedPage'])) ?? `${page}/embed`,
    preview: optStr(alias(o, ['preview', 'previewIndex'])) ?? `${page}/preview`,
    icon: optStr(o['icon']) ?? `${page}/icon.svg`,
    protectedResourceMetadata:
      optStr(o['protectedResourceMetadata']) ??
      page.replace(/\/t\/([^/]+)$/, '/.well-known/oauth-protected-resource/t/$1/mcp'),
  };
}

function billingPlan(raw: unknown, path: string): BillingPlan {
  const o = obj(raw, path);
  const price = alias(o, ['price', 'priceLabel', 'monthly']);
  return {
    id: str(o['id'], `${path}.id`),
    name: str(o['name'], `${path}.name`),
    callsIncluded: numOrNull(o['callsIncluded'], `${path}.callsIncluded`),
    crawls: numOrNull(o['crawls'], `${path}.crawls`),
    price: typeof price === 'string' ? price : typeof price === 'number' ? String(price) : null,
  };
}

/**
 * `GET /api/billing`.
 *
 * `configured`, `plan` and `usage` are required in every answer, including the
 * one a host with no Stripe key gives: "billing is off" is stated by the flag,
 * not implied by a missing plan. A `used` that defaulted to 0 would tell a
 * customer they have their whole allowance left, so it throws instead.
 */
export function readBilling(raw: unknown): Billing {
  const o = obj(raw, 'billing');
  const usage = obj(o['usage'], 'billing.usage');
  const sub = o['subscription'];
  const plans = o['plans'];
  return {
    configured: bool(o['configured'], 'billing.configured'),
    plan: billingPlan(o['plan'], 'billing.plan'),
    subscription:
      sub == null || typeof sub !== 'object'
        ? null
        : {
            status: str((sub as Record<string, unknown>)['status'], 'billing.subscription.status'),
            currentPeriodEnd: optStr((sub as Record<string, unknown>)['currentPeriodEnd']),
          },
    usage: {
      used: num(usage['used'], 'billing.usage.used'),
      remaining: numOrNull(usage['remaining'] ?? null, 'billing.usage.remaining'),
    },
    plans: Array.isArray(plans) ? plans.map((p, i) => billingPlan(p, `billing.plans[${i}]`)) : [],
  };
}

/** `POST /api/billing/checkout` and `/portal` both answer `{url}`. */
export function readRedirect(raw: unknown, path: string): string {
  const o = obj(raw, path);
  return str(o['url'], `${path}.url`);
}

function jobState(value: unknown, path: string): JobState {
  const s = str(value, path);
  if ((JOB_STATES as readonly string[]).includes(s)) return s as JobState;
  // A sixth state is a server this build does not know. Rendering it as
  // "queued" would leave a person watching a spinner over a job that is over.
  throw new ContractError(path, `one of ${JOB_STATES.join(', ')}`, s);
}

/**
 * `GET /api/onboard/:id`, and each row of `GET /api/onboard`.
 *
 * `state` and `draft` are required, not defaulted: they are the two fields the
 * screen chooses its wording from — "crawling", "ready to publish", "live" —
 * and a job read as queued when the server said failed is a person waiting for
 * a crawl that is never going to finish. `log` is a list of lines because the
 * route splits it; an absent log is an empty list, which is what a job that
 * has not started has to say.
 */
export function readOnboardJob(raw: unknown, path = 'job'): OnboardJob {
  const outer = obj(raw, path);
  // The single-job route answers `{ok, job:{…}}`; the list carries rows bare.
  const o = obj(outer['job'] ?? outer, path);
  const caps = o['capabilities'];
  return {
    id: str(alias(o, ['id', 'jobId']), `${path}.id`),
    url: str(o['url'], `${path}.url`),
    tenantId: str(o['tenantId'], `${path}.tenantId`),
    state: jobState(o['state'], `${path}.state`),
    phase: optStr(o['phase']) ?? '',
    progress: optNum(o['progress']),
    ownership: str(o['ownership'], `${path}.ownership`),
    error: optStr(o['error']),
    capabilities: typeof caps === 'number' && Number.isFinite(caps) ? caps : null,
    createdAt: str(o['createdAt'], `${path}.createdAt`),
    startedAt: optStr(o['startedAt']),
    finishedAt: optStr(o['finishedAt']),
    publishedAt: optStr(o['publishedAt']),
    draft: bool(o['draft'], `${path}.draft`),
    log: messages(o['log']),
  };
}

/** `GET /api/onboard`. A missing list is a moved contract, not "no crawls yet". */
export function readOnboardJobs(raw: unknown): OnboardJob[] {
  const o = obj(raw, 'onboard');
  return requiredList(o, ['jobs'], 'onboard.jobs').map((j, i) => readOnboardJob(j, `onboard.jobs[${i}]`));
}

export function readOnboardReceipt(raw: unknown): OnboardReceipt {
  const o = obj(raw, 'onboard');
  return {
    jobId: str(o['jobId'], 'onboard.jobId'),
    tenantId: str(o['tenantId'], 'onboard.tenantId'),
    state: jobState(o['state'], 'onboard.state'),
    deduplicated: optBool(o['deduplicated'], false),
    queuePosition: optNum(o['queuePosition']),
    note: optStr(o['note']),
  };
}

/**
 * The draft, reduced to what the review shows.
 *
 * The route also sends the whole manifest, which this screen does not render:
 * the review asks "which tools, under whose name, from which origin", and the
 * `summary` block answers exactly that. Reading the full manifest here would
 * duplicate the tenant screen for a tenant that does not exist yet.
 */
/**
 * `usefulness`, wherever the payload happens to carry it.
 *
 * Read defensively rather than through the manifest's own parser: this reader
 * runs against a DRAFT, which is the one manifest on this host that has not yet
 * been through `parseManifest`, and a score with a missing `reasons` array must
 * cost the review screen nothing worse than a shorter list.
 */
function usefulness(raw: unknown): DraftUsefulness | null {
  if (raw == null || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if (typeof o['score'] !== 'number') return null;
  const reasons = Array.isArray(o['reasons']) ? o['reasons'] : [];
  return {
    score: o['score'],
    threshold: optNum(o['threshold']),
    reasons: reasons.flatMap((r) => {
      if (r == null || typeof r !== 'object') return [];
      const e = r as Record<string, unknown>;
      const note = optStr(e['note']);
      if (!note) return [];
      return [{ signal: optStr(e['signal']) ?? 'signal', delta: optNum(e['delta']), note }];
    }),
  };
}

/**
 * The capabilities of the full manifest the route sends beside the summary,
 * keyed by name.
 *
 * The summary block is deliberately small — name, kind, title, view — and it
 * was written before the crawl learned to ship a tool switched off. Rather than
 * wait for that block to grow, the two fields that decide what a customer will
 * actually be served are lifted out of the manifest in the same response. A
 * host that later adds them to `summary` wins: the summary is read first.
 */
function draftManifestCaps(o: Record<string, unknown>): Map<string, Record<string, unknown>> {
  const out = new Map<string, Record<string, unknown>>();
  const manifest = o['manifest'];
  if (manifest == null || typeof manifest !== 'object') return out;
  const caps = (manifest as Record<string, unknown>)['capabilities'];
  if (!Array.isArray(caps)) return out;
  for (const c of caps) {
    if (c == null || typeof c !== 'object') continue;
    const cap = c as Record<string, unknown>;
    const name = alias(cap, ['name', 'id']);
    if (typeof name === 'string') out.set(name, cap);
  }
  return out;
}

/**
 * Whether an agent can reach this tool at all.
 *
 * Either surface counts, and absent means on: every manifest written before the
 * crawl scored anything ships everything. The crawl sets both flags together,
 * but a person editing a published tenant does not — a tool served on the
 * operator's own page and hidden from chat clients is a live tool — so the same
 * question is asked here and by `isRegistered` in `ui/capcard.ts`, and the two
 * have to answer it the same way or a draft and a published site disagree about
 * which of their tools are off.
 */
function registers(cap: Record<string, unknown> | undefined): boolean {
  const surfaces = cap?.['surfaces'];
  if (surfaces == null || typeof surfaces !== 'object') return true;
  const on = (key: string): boolean => {
    const surface = (surfaces as Record<string, unknown>)[key];
    if (surface == null || typeof surface !== 'object') return true;
    return (surface as Record<string, unknown>)['register'] !== false;
  };
  return on('web') || on('app');
}

export function readDraftSummary(raw: unknown): DraftSummary {
  const o = obj(raw, 'draft');
  const s = obj(o['summary'], 'draft.summary');
  const full = draftManifestCaps(o);
  return {
    jobId: str(o['jobId'], 'draft.jobId'),
    tenantId: str(o['tenantId'], 'draft.tenantId'),
    draft: bool(o['draft'], 'draft.draft'),
    displayName: str(s['displayName'], 'draft.summary.displayName'),
    origin: str(s['origin'], 'draft.summary.origin'),
    ownership: str(s['ownership'], 'draft.summary.ownership'),
    capabilities: arr(s['capabilities'], 'draft.summary.capabilities').map((c, i) => {
      const path = `draft.summary.capabilities[${i}]`;
      const cap = obj(c, path);
      const name = str(cap['name'], `${path}.name`);
      const whole = full.get(name);
      return {
        name,
        kind: optStr(cap['kind']) ?? '',
        title: optStr(cap['title']) ?? name,
        description: optStr(cap['description']) ?? '',
        view: optStr(cap['view']),
        register: cap['surfaces'] !== undefined ? registers(cap) : registers(whole),
        usefulness: usefulness(cap['usefulness'] ?? whole?.['usefulness']),
      };
    }),
    views: arr(s['views'] ?? [], 'draft.summary.views').map((v) => (typeof v === 'string' ? v : JSON.stringify(v))),
  };
}

/**
 * The merge lists, defaulted to empty rather than raised.
 *
 * A host that predates the field is not an error — the publish still happened
 * and both URLs are live — so this reads as "nothing reported", and the receipt
 * distinguishes that from "nothing carried" by whether the publish replaced
 * anything at all.
 */
function mergeReport(raw: unknown): MergeReport {
  if (raw == null || typeof raw !== 'object') return { carried: [], dropped: [] };
  const o = raw as Record<string, unknown>;
  return { carried: messages(o['carried']), dropped: messages(o['dropped']) };
}

/** `POST /api/onboard/:id/publish`. Both URLs are required: they are what "live" means. */
export function readPublishReceipt(raw: unknown): PublishReceipt {
  const o = obj(raw, 'publish');
  const urls = obj(o['urls'], 'publish.urls');
  const caps = o['capabilities'];
  return {
    tenantId: str(o['tenantId'], 'publish.tenantId'),
    displayName: optStr(o['displayName']),
    ownership: optStr(o['ownership']),
    capabilities: Array.isArray(caps) ? caps.map((c) => (typeof c === 'string' ? c : JSON.stringify(c))) : [],
    replaced: optBool(o['replaced'], false),
    edits: mergeReport(alias(o, ['edits', 'merge'])),
    urls: {
      web: str(urls['web'], 'publish.urls.web'),
      mcp: str(urls['mcp'], 'publish.urls.mcp'),
    },
  };
}

/* ── Transport ─────────────────────────────────────────────────────────── */

function apiBase(): string {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="clawharbor-api"]');
  const configured = meta?.content?.trim();
  if (configured) return configured.replace(/\/+$/, '');
  const override = new URLSearchParams(location.search).get('api');
  if (override) return override.replace(/\/+$/, '');
  return '';
}

function secretHeaderName(): string {
  const meta = document.querySelector<HTMLMetaElement>(
    'meta[name="clawharbor-reload-header"], meta[name="clawharbor-publish-header"]',
  );
  return (meta?.content?.trim() || DEFAULT_SECRET_HEADER).toLowerCase();
}

async function readError(res: Response): Promise<{ message: string; code: string }> {
  const text = await res.text().catch(() => '');
  if (!text) return { message: `${res.status} ${res.statusText}`, code: '' };
  try {
    const body = JSON.parse(text) as { error?: string; message?: string; code?: string };
    return {
      message: body.error ?? body.message ?? text.slice(0, 240),
      code: typeof body.code === 'string' ? body.code : '',
    };
  } catch {
    return { message: text.slice(0, 240), code: '' };
  }
}

const enc = encodeURIComponent;

export class DashboardApi {
  private readonly base = apiBase();

  /**
   * The operator secret, typed into the login screen's operator section.
   *
   * A field on a live object and nothing more. It is deliberately not in the
   * store's serialisable state and deliberately not persisted, so closing the
   * tab is the only sign-out the operator path needs — and there is nothing on
   * disk for the next person at the keyboard to find. An account session is not
   * held here at all: it is the server's httpOnly cookie.
   */
  secret = '';

  get hasSecret(): boolean {
    return this.secret.length > 0;
  }

  private async send(
    method: string,
    path: string,
    body?: unknown,
  ): Promise<{ json: unknown; ms: number }> {
    const headers: Record<string, string> = { accept: 'application/json' };
    if (this.secret) headers[secretHeaderName()] = this.secret;
    if (body !== undefined) headers['content-type'] = 'application/json';

    const started = performance.now();
    let res: Response;
    try {
      res = await fetch(`${this.base}${path}`, {
        method,
        headers,
        // The session cookie is httpOnly and set by the server on the callback;
        // this is the one line that makes it travel. 'same-origin' rather than
        // 'include' because the dashboard API is same-origin by construction,
        // and a cross-origin `?api=` override should not carry anyone's session.
        credentials: 'same-origin',
        ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      });
    } catch (err) {
      // Distinguished from a refusal on purpose: the screens say "the API is
      // unreachable" rather than inventing an empty fleet.
      throw new ApiError(
        `the dashboard API is unreachable (${err instanceof Error ? err.message : String(err)})`,
        0,
      );
    }
    const ms = Math.round(performance.now() - started);
    if (!res.ok) {
      const { message: detail, code } = await readError(res);
      // A 401 is left as the bare fact: the router turns it into the login
      // screen, and a sentence about the reload secret would be wrong for the
      // account whose cookie just expired.
      throw new ApiError(detail, res.status, code);
    }
    if (res.status === 204) return { json: {}, ms };
    const text = await res.text();
    if (!text) return { json: {}, ms };
    try {
      return { json: JSON.parse(text) as unknown, ms };
    } catch {
      throw new ContractError(path, 'a JSON body', text.slice(0, 120));
    }
  }

  /* ── Auth ────────────────────────────────────────────────────────────── */

  /**
   * Who the server thinks this page is, or null when nobody.
   *
   * A 401 is the ordinary answer on a fresh tab and is returned as null rather
   * than thrown, so the boot path has one branch: principal or login screen. A
   * 404 is treated the same way, because a host built without the accounts
   * routes still has an operator, and the operator section of the login screen
   * is where they would find out.
   */
  async me(): Promise<Principal | null> {
    try {
      const { json } = await this.send('GET', '/api/auth/me');
      return readPrincipal(json);
    } catch (err) {
      if (err instanceof ApiError && (err.status === 401 || err.status === 404)) return null;
      throw err;
    }
  }

  async login(email: string): Promise<LoginReceipt> {
    const { json } = await this.send('POST', '/api/auth/login', { email });
    return readLoginReceipt(json);
  }

  async logout(): Promise<void> {
    await this.send('POST', '/api/auth/logout', {});
  }

  /** Ends every session of this account except the one making the request. */
  async logoutAll(): Promise<number> {
    const { json } = await this.send('POST', '/api/auth/logout-all', {});
    return optNum(obj(json, 'logout-all')['ended']);
  }

  /* ── Two-step sign-in ────────────────────────────────────────────────── */

  async twoFactor(): Promise<TwoFactorStatus> {
    const { json } = await this.send('GET', '/api/auth/2fa');
    return readTwoFactorStatus(json);
  }

  async twoFactorSetup(): Promise<TwoFactorEnrolment> {
    const { json } = await this.send('POST', '/api/auth/2fa/setup', {});
    return readTwoFactorEnrolment(json);
  }

  async twoFactorConfirm(code: string): Promise<string[]> {
    const { json } = await this.send('POST', '/api/auth/2fa/confirm', { code });
    return readRecoveryCodes(json, '2fa.confirm');
  }

  async twoFactorDisable(code: string): Promise<void> {
    await this.send('POST', '/api/auth/2fa/disable', { code });
  }

  async twoFactorRecoveryCodes(code: string): Promise<string[]> {
    const { json } = await this.send('POST', '/api/auth/2fa/recovery-codes', { code });
    return readRecoveryCodes(json, '2fa.recovery');
  }

  /* ── Billing ─────────────────────────────────────────────────────────── */

  /**
   * `GET /api/billing`. A 503 `billing_disabled` is an answer, not an outage:
   * it is what a host with no Stripe key says, and the screen renders it as
   * "not configured" with the buttons dead. Any other failure still throws.
   */
  async billing(): Promise<Billing | 'disabled'> {
    try {
      const { json } = await this.send('GET', '/api/billing');
      return readBilling(json);
    } catch (err) {
      if (err instanceof ApiError && err.status === 503 && err.code === 'billing_disabled') return 'disabled';
      throw err;
    }
  }

  async checkout(plan: string): Promise<string> {
    const { json } = await this.send('POST', '/api/billing/checkout', { plan });
    return readRedirect(json, 'checkout');
  }

  async portal(): Promise<string> {
    const { json } = await this.send('POST', '/api/billing/portal', {});
    return readRedirect(json, 'portal');
  }

  /* ── Dashboard ───────────────────────────────────────────────────────── */

  async overview(): Promise<Overview> {
    const { json } = await this.send('GET', '/api/dashboard/overview');
    return readOverview(json);
  }

  async tenant(tenantId: string): Promise<TenantDetail> {
    const { json } = await this.send('GET', `/api/dashboard/tenants/${enc(tenantId)}`);
    return readTenantDetail(json, tenantId);
  }

  async usage(days: number, tenantId?: string | null): Promise<UsageWindow> {
    const q = new URLSearchParams({ days: String(days) });
    if (tenantId) q.set('tenantId', tenantId);
    const { json } = await this.send('GET', `/api/dashboard/usage?${q.toString()}`);
    return readUsage(json);
  }

  async health(tenantId: string): Promise<TenantHealth> {
    const { json } = await this.send('GET', `/api/dashboard/tenants/${enc(tenantId)}/health`);
    return readTenantHealth(json);
  }

  /** Runs a real probe against the live origin. Slow on purpose; never faked. */
  async checkHealth(tenantId: string): Promise<TenantHealth> {
    const { json } = await this.send('POST', `/api/dashboard/tenants/${enc(tenantId)}/health`, {});
    return readTenantHealth(json);
  }

  async patchTool(tenantId: string, tool: string, patch: ToolPatch): Promise<void> {
    await this.send(
      'PATCH',
      `/api/dashboard/tenants/${enc(tenantId)}/tools/${enc(tool)}`,
      patch,
    );
  }

  async deleteTool(tenantId: string, tool: string): Promise<void> {
    await this.send('DELETE', `/api/dashboard/tenants/${enc(tenantId)}/tools/${enc(tool)}`);
  }

  async runTool(
    tenantId: string,
    tool: string,
    args: Record<string, unknown>,
  ): Promise<RunResult> {
    const { json, ms } = await this.send(
      'POST',
      `/api/dashboard/tenants/${enc(tenantId)}/tools/${enc(tool)}/test`,
      { arguments: args },
    );
    return readRunResult(json, ms);
  }

  async patchView(tenantId: string, viewId: string, patch: ViewPatch): Promise<void> {
    await this.send('PATCH', `/api/dashboard/tenants/${enc(tenantId)}/views/${enc(viewId)}`, patch);
  }

  /** The tenant's widget stylesheet. Null clears it; a refused one is a 422 naming why. */
  async patchBrand(tenantId: string, customCss: string | null): Promise<void> {
    await this.send('PATCH', `/api/dashboard/tenants/${enc(tenantId)}/brand`, { customCss });
  }

  /** The hostnames declared to proxy /mcp here. The whole list, replacing the old one. */
  async patchIntegration(tenantId: string, proxyHosts: string[]): Promise<void> {
    await this.send('PATCH', `/api/dashboard/tenants/${enc(tenantId)}/integration`, { proxyHosts });
  }

  async unpublish(tenantId: string): Promise<void> {
    await this.send('POST', `/api/dashboard/tenants/${enc(tenantId)}/unpublish`, {});
  }

  /* ── Domain ownership ────────────────────────────────────────────────── */

  /**
   * The `/api/ownership/*` routes, called from the dashboard at last.
   *
   * They are NOT under `/api/dashboard`, and they are unauthenticated by design:
   * the proof is the authentication, and a token is a string until it appears in
   * the domain's DNS or on the site itself. Nothing here sends the session for
   * authority — `send` carries it because every request from this page does —
   * and nothing here publishes. Verifying changes what MAY be published; a
   * person still presses publish.
   *
   * The token comes back exactly once, in `challenge()`, and is held in memory
   * on the screen that asked for it. It is never stored and never re-fetched,
   * which is why the panel says so in the words the API itself used.
   */
  async ownership(tenantId: string): Promise<OwnershipStatus> {
    const { json } = await this.send('GET', `/api/ownership/${enc(tenantId)}`);
    return readOwnershipStatus(json);
  }

  async ownershipChallenge(tenantId: string): Promise<ProofChallenge> {
    const { json } = await this.send('POST', `/api/ownership/${enc(tenantId)}/challenge`, {});
    return readOwnershipChallenge(json);
  }

  async ownershipVerify(tenantId: string, challengeId?: string): Promise<ProofVerification> {
    const { json } = await this.send(
      'POST',
      `/api/ownership/${enc(tenantId)}/verify`,
      challengeId ? { challengeId } : {},
    );
    return readOwnershipVerification(json);
  }

  /**
   * "Is it STILL true" for a claim already on record — the renewal path.
   *
   * Separate from `verify` because it needs no challenge and issues no token: it
   * re-reads the proof the customer left in place. This is the call that makes
   * renewing a claim cost one button instead of a fresh crawl of the origin.
   */
  async ownershipRecheck(tenantId: string): Promise<ProofVerification> {
    const { json } = await this.send('POST', `/api/ownership/${enc(tenantId)}/recheck`, {});
    return readOwnershipVerification(json);
  }

  /* ── Onboarding ──────────────────────────────────────────────────────── */

  /**
   * `POST /api/onboard`. The session cookie travels with it, which is what
   * ties the job to the account: the server records the account on the job at
   * submit and checks a later `/publish` against it. The operator header goes
   * too when a secret is held, and a submission with neither is a job nobody
   * owns — the operator publishes those.
   */
  async startOnboard(url: string, ownership: Ownership): Promise<OnboardReceipt> {
    const { json } = await this.send('POST', '/api/onboard', { url, ownership });
    return readOnboardReceipt(json);
  }

  async onboardJob(jobId: string): Promise<OnboardJob> {
    const { json } = await this.send('GET', `/api/onboard/${enc(jobId)}`);
    return readOnboardJob(json);
  }

  async onboardJobs(): Promise<OnboardJob[]> {
    const { json } = await this.send('GET', '/api/onboard');
    return readOnboardJobs(json);
  }

  async draftSummary(jobId: string): Promise<DraftSummary> {
    const { json } = await this.send('GET', `/api/onboard/${enc(jobId)}/manifest`);
    return readDraftSummary(json);
  }

  /**
   * `POST /api/onboard/:id/publish`. Refusals arrive as `ApiError` with the
   * server's `code` (`ownership_unproven`, `third_party_requires_operator`,
   * `unknown_job`, `publish_disabled`) and its sentence, which the screen
   * prints verbatim; nothing here rewrites a refusal into something softer.
   */
  async publishJob(jobId: string): Promise<PublishReceipt> {
    const { json } = await this.send('POST', `/api/onboard/${enc(jobId)}/publish`, {});
    return readPublishReceipt(json);
  }

  async cancelJob(jobId: string): Promise<OnboardJob> {
    const { json } = await this.send('POST', `/api/onboard/${enc(jobId)}/cancel`, {});
    return readOnboardJob(json);
  }
}

export function createDashboardApi(): DashboardApi {
  return new DashboardApi();
}
